/**
 * TodoCache - AsyncStorage-backed cache of the user's task lists and tasks.
 * Keys are scoped by user id so multiple accounts on the same device don't
 * bleed into each other.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import type { Task } from '@/types/tasks';
import type { TaskList } from '@/types/task-lists';

const LISTS_KEY_PREFIX = 'todo.cache.lists.';
const TASKS_KEY_PREFIX = 'todo.cache.tasks.';

function listsKey(userId: string): string {
  return `${LISTS_KEY_PREFIX}${userId}`;
}

function tasksKey(userId: string, listId: string): string {
  return `${TASKS_KEY_PREFIX}${userId}.${listId}`;
}

async function readJson<T>(key: string): Promise<T | null> {
  try {
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

async function writeJson<T>(key: string, value: T): Promise<void> {
  try {
    await AsyncStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('[TodoCache] persist failed:', error);
  }
}

export class TodoCache {
  static getLists(userId: string): Promise<TaskList[] | null> {
    return readJson<TaskList[]>(listsKey(userId));
  }

  static setLists(userId: string, lists: TaskList[]): Promise<void> {
    return writeJson(listsKey(userId), lists);
  }

  static getTasks(userId: string, listId: string): Promise<Task[] | null> {
    return readJson<Task[]>(tasksKey(userId, listId));
  }

  static setTasks(userId: string, listId: string, tasks: Task[]): Promise<void> {
    return writeJson(tasksKey(userId, listId), tasks);
  }
}
